import { ADD_PAYMENT_METHOD, validateCreditCardFormValues } from './validateCreditCardFormValues';
import {
  CVVHostedField,
  cvvRevalidationArea,
  mobileOnlyDivider,
  threeUpFields,
} from 'forms/creditCardFormCssDefs';
import {
  Divider,
  FormBlock,
  FormFormattedTextField,
  FormSelectField,
  FormTextField,
  SubmitButton,
  Typography,
} from 'omakase';
import { Form, Formik, FormikHelpers, setNestedObjectValues } from 'formik';
import { FormHelperText } from '@material-ui/core';
import { FunctionComponent, ReactNode, createElement, useCallback, useState } from 'react';
import { MenuItem } from 'ui';
import { UnknownIcon } from '@tock/shared/components/icons/CreditCardIcons';
import { addCvvToken, cardFromNumber } from '@tock/shared/utils/creditCards';
import { core } from '@tock/coretypes';
import {
  creditCardExpiryFilter,
  creditCardNumberFilter,
} from '@tock/shared/utils/creditCardFilters';
import { getAppPatron } from 'selectors/appSelector';
import { getDeviceData } from 'selectors/paymentCardSelectors';
import { toNumber } from 'es-toolkit/compat';
import { useActions } from '@tock/shared/utils/connect';
import { useAppSettings } from 'selectors/settingsSelector';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useSelector } from 'react-redux';
import CreditCardBrandMapping from '@tock/shared/constants/CreditCardBrandMapping';
import PaymentScriptLoader from '@tock/shared/components/render-prop-components/PaymentScriptLoader';
import TurnstileValidator, { isTurnstileActive } from 'forms/TurnstileValidator';
import valid from 'card-validator';

const creditCardFormIconSelectStyle = { marginRight: '8px' };

export interface CreditCardFormValues {
  cvc: string;
  expiry: string;
  name: string;
  number: string;
  zipCode: string;
  selectedCardId: string;
  billingAddressSameAsShipping: boolean;
  challengeResponse: string;
}
export interface CreditCardFormProps {
  cards?: core.PaymentCard[];
  doPresubmit?(): Promise<void>;
  onValidationResultsAtSubmitTime?(hasErrors: boolean): void;
  onSubmit(card: core.PaymentCard, shouldAttachCvvNonce?: boolean): Promise<unknown>;
  revalidateSavedCreditCards?: boolean;
  submitLabel?: string;
  /**
   * Specify this to replace the submit button with some kind of alternate content,
   * e.g., a disabled loading button with an internal spinner.
   */
  alternateButtonContent?: ReactNode;
  /**
   * Specify this if there is content that should appear after the button.
   */
  afterButtonContent?: ReactNode;
  disableSubmit?: boolean;
  formActionsContent?: ReactNode;
  includeBillingAddressFields?: boolean;
  onSubmitBillingAddressFields?(values: { billingAddressSameAsShipping: boolean }): void;
  title?: string;
  isSubmittingExternal?: boolean;
  requireTurnstile?: boolean;
}

export const CreditCardForm: FunctionComponent<CreditCardFormProps> = ({
  cards,
  doPresubmit,
  onSubmit,
  onValidationResultsAtSubmitTime,
  disableSubmit,
  revalidateSavedCreditCards = false,
  submitLabel,
  formActionsContent,
  title,
  alternateButtonContent,
  afterButtonContent,
  isSubmittingExternal,
  requireTurnstile = false,
}) => {
  const settings = useAppSettings();

  const me = useSelector(getAppPatron);

  const { actions } = useActions();

  const [brainTreeError, setBrainTreeError] = useState(false);

  const deviceData = useSelector(getDeviceData);

  const isTurnstileFlagEnabled = useFeatureFlag('TURNSTILE_RENDER_CLIENT');
  const isStripeForwardApiEnabled = useFeatureFlag('STRIPE_FORWARD_API');

  const includeTurnstileValidation = requireTurnstile && isTurnstileActive(isTurnstileFlagEnabled);

  const setValidateCVCRef = useCallback(
    async (cvcField: HTMLElement) => {
      if (window.DO_NOT_LOAD_PAYMENT_SCRIPTS_BECAUSE_WE_ARE_IN_TEST) {
        return;
      }
      // Clear anything on the window related to any previous braintree hosted fields.
      delete window.braintreeHostedField;
      try {
        await actions.paymentCard.createBraintreeHostedField();
        setBrainTreeError(!window.braintreeHostedField);
      } catch (_error) {
        setBrainTreeError(true);
      }
    },
    [actions.paymentCard]
  );

  const [hasPhoneError] = useState(false);

  const handleSubmit = async (
    values: CreditCardFormValues,
    formikHelpers: FormikHelpers<CreditCardFormValues>,
    revalidateSavedCreditCards: boolean
  ) => {
    if (!values.billingAddressSameAsShipping && hasPhoneError) {
      // hasPhoneError won't have been handled through the usual form validation mechanism,
      // but it is a case where there is a validation problem that should prevent us from proceeding.
      // Should we encounter this, call back on the onValidationResultsAtSubmitTime callback (if any)
      // and return from this helper without doing any work.
      onValidationResultsAtSubmitTime?.(true);
      return;
    }

    // existing card and revalidating security code
    if (values.selectedCardId !== ADD_PAYMENT_METHOD) {
      const selectedCard =
        cards && cards.length > 0
          ? cards.find((card) => card.id === toNumber(values.selectedCardId))
          : undefined;
      if (!selectedCard) {
        return;
      }
      if (window.braintreeHostedField && revalidateSavedCreditCards) {
        const card = await addCvvToken(selectedCard);
        await onSubmit(card as core.PaymentCard, true);
      } else {
        await onSubmit(selectedCard as core.PaymentCard, true);
      }
    } else {
      const newCard = {
        cvc: values.cvc,
        expiry: values.expiry,
        name: values.name,
        number: values.number,
        zipCode: values.zipCode,
        deviceData,
        skipAvsCheck: false,
      };

      const tokenizeAndSaveMethod = isStripeForwardApiEnabled
        ? actions.paymentCard.tokenizeAndSaveCardWithStripeForwarding
        : actions.paymentCard.tokenizeAndSaveCard;

      const { payload: card } = await tokenizeAndSaveMethod(newCard, {
        challengeResponse: values.challengeResponse,
        requireTurnstile: includeTurnstileValidation,
      });
      await onSubmit(card as core.PaymentCard, false);
    }
    formikHelpers.setSubmitting(false);
  };

  return (
    <PaymentScriptLoader
      stripePublicKey={settings.STRIPE_PUBLIC_KEY}
      deviceData={deviceData}
      nonBlocking={false}
    >
      <Formik<CreditCardFormValues>
        initialValues={{
          cvc: '',
          expiry: '',
          name: `${me?.firstName} ${me?.lastName}`,
          number: '',
          zipCode: '',
          selectedCardId: cards && cards.length > 0 ? `${cards[0].id}` : ADD_PAYMENT_METHOD,
          billingAddressSameAsShipping: true,
          challengeResponse: '',
        }}
        onSubmit={(values, formikHelpers) =>
          handleSubmit(values, formikHelpers, revalidateSavedCreditCards)
        }
        validate={(values) => {
          const creditCardErrors = validateCreditCardFormValues(values, revalidateSavedCreditCards);

          if (includeTurnstileValidation && !values.challengeResponse) {
            creditCardErrors.challengeResponse = 'Please complete captcha.';
          }
          return { ...creditCardErrors };
        }}
      >
        {({
          values,
          isSubmitting,
          errors,
          touched,
          setErrors,
          setTouched,
          submitForm,
          validateForm,
          setFieldValue,
        }) => {
          let creditCardIcon;
          let cvcLength;
          if (values.number) {
            const card = cardFromNumber(values.number);
            if (!card) {
              throw new Error('unknown card');
            }

            const { component } = CreditCardBrandMapping[card.type];

            creditCardIcon = createElement(component, {
              style: { fontSize: '3rem' },
            });

            const numberValidation = valid.number(values.number);
            cvcLength = numberValidation.card ? numberValidation.card.code.size : 4;
          }

          const hideCvvRevalidationArea =
            values.selectedCardId === ADD_PAYMENT_METHOD ||
            !revalidateSavedCreditCards ||
            brainTreeError;

          return (
            <Form className="FSHide" data-testid="credit-card-form">
              <FormBlock box titleText={title ?? undefined} showDivider={false}>
                {cards && cards.length > 0 ? (
                  <>
                    {/* need to filter the cards value in a selector maybe? other one does for a unique list */}
                    {/* need to figure out the pattern part of the inputs, not working the same way as redux form based one */}
                    <FormSelectField
                      variant="inverse"
                      name="selectedCardId"
                      id="selectedCardId"
                      fullWidth
                      data-testid="credit-card-selected-card"
                      label="Select credit card"
                      native={false}
                      onChange={(event) => {
                        // don't clear out selectedCardId, only the other fiels.
                        setErrors({
                          cvc: '',
                          name: '',
                          number: '',
                          expiry: '',
                          zipCode: '',
                        });
                        setTouched({
                          cvc: false,
                          name: false,
                          number: false,
                          expiry: false,
                          zipCode: false,
                        });
                      }}
                    >
                      <MenuItem value={ADD_PAYMENT_METHOD}>
                        <UnknownIcon style={creditCardFormIconSelectStyle} />
                        Add new payment method
                      </MenuItem>
                      {cards.map((card) => {
                        const { component: Icon, maskClass } = CreditCardBrandMapping[card.brand];
                        return (
                          <MenuItem key={card.id} value={card.id}>
                            <Icon style={creditCardFormIconSelectStyle} />
                            {maskClass === 'is-mask-one' ? (
                              <>•••• •••••• •</>
                            ) : (
                              <>•••• •••• ••••</>
                            )}{' '}
                            {card.maskedNumber}
                          </MenuItem>
                        );
                      })}
                    </FormSelectField>
                  </>
                ) : null}
                {/*
                 * The strategy here is to always, *always* render the CVV revalidation area, but just to have it be hidden as appropriate.
                 * Hooking into the braintree hosted fields mechanism is handled via setValidateCVCRef.
                 *
                 * Whether any data in the CVC hosted field is actually used is entirely deferred to the handleSubmit implementation.
                 */}
                <div css={cvvRevalidationArea({ hidden: hideCvvRevalidationArea })}>
                  <Typography variant="body02" component="p" mb={2}>
                    Please confirm your credit card security code
                  </Typography>
                  <span className="FSHide" css={CVVHostedField} id="cvv" ref={setValidateCVCRef} />
                  {errors.cvc && touched.cvc ? (
                    <FormHelperText error>{errors.cvc}</FormHelperText>
                  ) : null}
                </div>
                {(values.selectedCardId === ADD_PAYMENT_METHOD || !values.selectedCardId) && (
                  <FormBlock nested>
                    <FormTextField
                      autoComplete="name"
                      autoCorrect="off"
                      inputProps={{
                        'data-testid': 'credit-card-name-input',
                      }}
                      fullWidth
                      id="name"
                      label="Name on card"
                      name="name"
                      placeholder="Name on card"
                      variant="inverse"
                    />

                    <FormFormattedTextField
                      autoComplete="cc-number"
                      autoCorrect="off"
                      filter={creditCardNumberFilter}
                      fullWidth
                      id="number"
                      endAdornment={creditCardIcon}
                      // inputProps={{ maxLength: 20, pattern: '\d*' }}
                      inputProps={{
                        maxLength: 20,
                        'data-testid': 'credit-card-number-input',
                      }}
                      label="Card number"
                      name="number"
                      placeholder="Card number"
                      type="tel"
                      variant="inverse"
                    />

                    <div css={threeUpFields}>
                      <FormFormattedTextField
                        autoComplete="cc-exp"
                        filter={creditCardExpiryFilter}
                        fullWidth
                        id="expiry"
                        // inputProps={{ maxLength: 7, pattern: '\d*' }}
                        inputProps={{ maxLength: 7, 'data-testid': 'credit-card-expiry-input' }}
                        label="Expiration date"
                        name="expiry"
                        placeholder="MM / YY"
                        type="tel"
                        variant="inverse"
                      />
                      <FormTextField
                        autoComplete="cc-csc"
                        autoCorrect="off"
                        fullWidth
                        id="cvc"
                        // inputProps={{ maxLength: cvcLength, pattern: '\d*' }}
                        inputProps={{
                          maxLength: cvcLength,
                          'data-testid': 'credit-card-cvc-input',
                        }}
                        label="Security code"
                        name="cvc"
                        placeholder="CVC"
                        type="tel"
                        variant="inverse"
                      />
                      <FormTextField
                        autoComplete="postal-code"
                        autoCorrect="off"
                        inputProps={{ 'data-testid': 'credit-card-zipcode-input' }}
                        fullWidth
                        id="zipCode"
                        label="Zip/Postal code"
                        name="zipCode"
                        placeholder="Zip/Postal code"
                        variant="inverse"
                      />
                    </div>
                  </FormBlock>
                )}
              </FormBlock>
              <Divider variant="thick" css={mobileOnlyDivider} />
              {includeTurnstileValidation && (
                <TurnstileValidator
                  onReceiveChallengeResponse={(challengeResponse) => {
                    setFieldValue('challengeResponse', challengeResponse);
                  }}
                />
              )}
              <FormBlock showDivider={false}>
                {formActionsContent ? formActionsContent : null}
                {alternateButtonContent ?? (
                  <SubmitButton
                    size="large"
                    variant="primary"
                    type="button"
                    onClick={async () => {
                      await doPresubmit?.();
                      if (onValidationResultsAtSubmitTime) {
                        const errors = await validateForm(values);
                        setTouched(setNestedObjectValues(errors, true));
                        const hasErrors = Object.keys(errors).length > 0;
                        onValidationResultsAtSubmitTime(hasErrors);
                        if (hasErrors) {
                          return;
                        }
                      }
                      submitForm();
                    }}
                    isSubmitting={isSubmittingExternal || isSubmitting}
                    fullWidth
                    data-testid="complete-purchase-button"
                    disabled={disableSubmit}
                  >
                    {submitLabel ? submitLabel : 'Complete purchase'}
                  </SubmitButton>
                )}
                {afterButtonContent}
              </FormBlock>
            </Form>
          );
        }}
      </Formik>
    </PaymentScriptLoader>
  );
};
